const loadedScripts = new Map();

const pickModuleCandidate = (value, predicate) => {
  if (!value) return null;

  const candidates = [value, value?.default, value?.Plot, value?.default?.Plot];
  for (const candidate of candidates) {
    if (candidate && predicate(candidate)) {
      return candidate;
    }
  }

  return null;
};

const normalizePlot = (value) => {
  const candidate = pickModuleCandidate(
    value,
    (lib) =>
      typeof lib.plot === 'function' &&
      (typeof lib.barY === 'function' || (lib.marks && typeof lib.marks.barY === 'function')),
  );

  if (!candidate) {
    throw new Error('Observable Plot library failed to expose the expected API.');
  }

  if (typeof candidate.barY === 'function') {
    return candidate;
  }

  // Some builds only expose barY under the marks namespace; provide a stable alias.
  const alias = Object.create(Object.getPrototypeOf(candidate) || Object.prototype);
  return Object.assign(alias, candidate, { barY: candidate.marks.barY });
};

const normalizeD3 = (value) => {
  const candidate = pickModuleCandidate(
    value,
    (lib) => typeof lib.select === 'function' && typeof lib.csv === 'function',
  );

  if (!candidate) {
    throw new Error('D3 library failed to expose the expected API.');
  }

  return candidate;
};

const normalizers = {
  Plot: normalizePlot,
  d3: normalizeD3,
};

const resolveGlobal = (globalName) => {
  if (!globalName) return undefined;

  const value = window[globalName];
  if (value == null) {
    throw new Error(`Global ${globalName} was not found on window.`);
  }

  const normalizer = normalizers[globalName];
  return normalizer ? normalizer(value) : value;
};

const waitForGlobal = (globalName, attempts = 50, delay = 100) => {
  if (!globalName) {
    return Promise.resolve();
  }

  const attemptResolve = (remainingAttempts) =>
    Promise.resolve()
      .then(() => resolveGlobal(globalName))
      .catch((error) => {
        if (remainingAttempts <= 0) {
          return Promise.reject(error);
        }

        return new Promise((resolve) => {
          setTimeout(resolve, delay);
        }).then(() => attemptResolve(remainingAttempts - 1));
      });

  return attemptResolve(attempts);
};

export function loadExternalScript(src, globalName) {
  if (typeof window === 'undefined') {
    return Promise.reject(new Error('Window is not available'));
  }

  if (globalName && window[globalName]) {
    return waitForGlobal(globalName);
  }

  if (loadedScripts.has(src)) {
    return loadedScripts.get(src);
  }

  const promise = new Promise((resolve, reject) => {
    const script = document.createElement('script');
    script.src = src;
    script.async = true;
    script.onload = () => {
      waitForGlobal(globalName)
        .then(resolve)
        .catch((error) => {
          loadedScripts.delete(src);
          reject(error);
        });
    };
    script.onerror = () => {
      loadedScripts.delete(src);
      reject(new Error(`Failed to load script: ${src}`));
    };
    document.head.appendChild(script);
  });

  loadedScripts.set(src, promise);
  return promise;
}
